import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import usePrice from 'src/hooks/usePrice';
import {
  setSelectedDisclaimer,
  openModal,
  setPrintActions,
  previewAccessory,
  setSelectedProvince,
} from 'src/redux/configurator/actions';
import InfoCircle from 'src/assets/icons/info-circle.svg';
import Label from 'components/_utility/Label';
import OfferBanner from 'components/OfferBanner';
import useBikeTotal from 'src/hooks/useBikeTotal';
import useMobileDetect from 'src/hooks/useMobileDetect';
import {
  bikeModelSelector,
  bikeColourSelector,
  getSelectedServicePack,
  getSelectedWarrantyPack,
} from 'src/redux/bike/selectors';
import CalculatorIcon from 'src/assets/icons/calculator.svg';
import { removeServicePack, addServicePack, removeWarrantyPack, addWarrantyPack } from 'src/redux/bike/actions';
import { ctaSelector, getGoalId, getLabels, getProvincePricingEnabled } from 'src/redux/app/selectors';
import { interceptLinkClick, isIOS, getLanguageCode, updateLinkSubmit, removeDuplicateParams } from 'helpers/utilities';
import useDataLayer from 'src/hooks/useDataLayer';
import useOpenFinance from 'src/hooks/useOpenFinance';
import useLaunchSendToDealer from 'src/hooks/useLaunchSendToDealer';
import { getConfigurations, getLoadedConfiguration, getSaving } from 'src/redux/garage/selectors';
import fetchGoal from 'helpers/goals';
import { useGeneratePDF } from 'src/hooks/useGeneratePDF';
import { getGarageEmailAddress } from 'helpers/garage';
import useSaveToGarage from 'src/hooks/useSaveToGarage';
import { getPrintActions, accessoryExtrasSelector, getSelectedProvince } from 'src/redux/configurator/selectors';
import { getInteractions } from 'src/redux/tagging/selectors';
import { useNavigate } from 'helpers/routing';
import { getImageBaseUrl } from 'helpers/request';
import { setSaving } from 'src/redux/garage/actions';
import ProvinceSelector from 'src/components/_form/ProvinceSelector/ProvinceSelector';
import useProvincialFees from 'src/hooks/useProvincialFees';
import {
  Wrap,
  BikeTitle,
  TabsContainer,
  Tab,
  TabContent,
  TotalTitle,
  TotalTitleLeft,
  SelectProvinceText,
  SelectedProvinceText,
  RegularValue,
  TotalSellingPriceValue,
  TotalWrap,
  Total,
  InfoCircleWrap,
  CalculateFinanceButton,
  Extras,
  ExtrasTitle,
  Extra,
  ExtraIconWrap,
  ExtraIcon,
  ExtraDetails,
  ExtraName,
  ExtraPrice,
  ExtraCTA,
  AdditionalCTAWrap,
  AdditionalCTA,
  Actions,
  Action,
  ActionIcon,
  ActionLabelWrap,
  PopupLabel,
  Separator,
} from './SummaryActions.styled';

const SummaryActions = ({ mobileOnly, desktopOnly, isSimulationChecksumExists, openShareModal }) => {
  const formatPrice = usePrice();
  const dispatch = useDispatch();
  const total = useBikeTotal();
  const pushToDataLayer = useDataLayer();
  const openFinance = useOpenFinance();
  const launchSendToDealer = useLaunchSendToDealer();
  const generatePDF = useGeneratePDF();
  const saveToGarage = useSaveToGarage();
  const navigate = useNavigate();
  const configurations = useSelector(getConfigurations);
  const interactions = useSelector(getInteractions);
  const loadedConfiguration = useSelector(getLoadedConfiguration);
  const bikeModel = useSelector(bikeModelSelector);
  const bikeColour = useSelector(bikeColourSelector);
  const ctaLinks = useSelector(ctaSelector);
  const garageSaving = useSelector(getSaving);
  // const sendToDealerData = useSelector(getSendToDealerData);
  const printActions = useSelector(getPrintActions);
  const extras = useSelector(accessoryExtrasSelector);

  const provincePricingEnabled = useSelector(getProvincePricingEnabled);
  const labels = useSelector(getLabels);
  const selectedProvince = useSelector(getSelectedProvince);

  const [activeTab, setActiveTab] = useState('addons');
  const [isProvinceDropdownOpen, setIsProvinceDropdownOpen] = useState(false);

  const { fees: provincialFeesRaw } = useProvincialFees(selectedProvince?.id, bikeModel);
  const provincialFees = provincePricingEnabled && selectedProvince ? provincialFeesRaw : null;
  const selectedServicePack = useSelector(getSelectedServicePack);
  const selectedWarrantyPack = useSelector(getSelectedWarrantyPack);
  const saveGoalId = useSelector(getGoalId('save configuration to garage'));
  const testRideGoalId = useSelector(getGoalId('cta - Test ride - summary page'));
  const printGoalId = useSelector(getGoalId('cta - print - summary page'));
  const feesTotal = extras?.fees?.reduce((prevTotal, fee) => prevTotal + Number(fee?.price || 0), 0);

  const hasAddOns = extras?.servicingPacks?.length > 0 || extras?.warrantyPacks?.length > 0;
  const hasAddOnsTabLabel = labels.addOnsTabLabel;
  const hasSummaryTabLabel = labels.summaryTabLabel;
  const showTabs = provincePricingEnabled && hasAddOns && hasAddOnsTabLabel && hasSummaryTabLabel;

  const provincialFeesTotal =
    provincePricingEnabled && provincialFees ? (provincialFees.dealerFee || 0) + (provincialFees.freightFee || 0) : 0;

  const dealerFeeObject = extras?.fees?.find(
    fee => fee.title?.toLowerCase().includes('dealer') || fee.title?.toLowerCase().includes('pdi')
  );
  const freightFeeObject = extras?.fees?.find(fee => fee.title?.toLowerCase().includes('freight'));

  const totalPriceIncludingFees =
    provincePricingEnabled && selectedProvince ? total + provincialFeesTotal : total + feesTotal;

  const garageAction = loadedConfiguration
    ? {
        id: 'garage',
        label: 'garageLabel',
        icon: '/images/configurator/config-tray/Garage.svg',
        count: configurations.length,
        onClick: () => {
          pushToDataLayer('configuratorGarageAttempt');

          if (interactions.total) {
            return dispatch(openModal('unsavedConfiguration'));
          }

          if (!getGarageEmailAddress()) {
            return dispatch(openModal('loadGarage'));
          }

          navigate('/garage');
        },
      }
    : {
        id: 'save',
        label: 'saveToGarageLabel',
        icon: '/images/configurator/config-tray/Save.svg',
        onClick: async () => [
          pushToDataLayer('configuratorSaveAttempt'),
          getGarageEmailAddress() ? [await fetchGoal(saveGoalId), saveToGarage()] : dispatch(openModal('saveToGarage')),
        ],
        disabled: garageSaving,
      };
  const testBikeBaseUrl = ctaLinks.testRide?.includes('ColourCode')
    ? ctaLinks.testRide
    : ctaLinks.testRide + bikeModel.familyId;
  const bikeLegacy = bikeModel.canTestRide && ctaLinks.testRide ? testBikeBaseUrl : null;

  const testDriververride = bikeModel.testRideOverride;
  const testDriverButtonUrl = testDriververride ? testDriververride.url : bikeLegacy;

  const options = [
    garageAction,
    {
      id: 'testRide',
      label: 'requestTestRideLabel',
      icon: '/images/configurator/config-tray/Bike.svg',
      link: bikeModel.canTestRide
        ? updateLinkSubmit({
            link: testDriverButtonUrl,
            productCode: bikeModel?.productCode,
            colourCode: bikeColour?.colourCode,
            configurationID: loadedConfiguration?.id,
            provinceId: selectedProvince?.id,
          })
        : null,
      onClick: async event => [
        await fetchGoal(testRideGoalId),
        interceptLinkClick(event, () => pushToDataLayer('configuratorTestRide')),
      ],
      hidden: !ctaLinks.testRide || !bikeModel.canTestRide,
    },
  ];

  const shareAndPrintOptions = [
    {
      id: 'share',
      label: 'shareLabel',
      icon: '/images/configurator/config-tray/Share.svg',
      onClick: () => [pushToDataLayer('configuratorShareAttempt'), openShareModal()],
    },
    {
      id: 'print',
      label: 'printLabel',
      icon: '/images/configurator/bike-image/printer.svg',
      onClick: () => [
        dispatch(setPrintActions(printActions + 1)),
        pushToDataLayer('configuratorPrint'),
        fetchGoal(printGoalId),
        generatePDF(),
      ],
    },
  ];

  const buyNowUrl = updateLinkSubmit({
    link: ctaLinks.buyNow,
    productCode: bikeModel?.productCode,
    colourCode: bikeColour?.colourCode,
    configurationID: loadedConfiguration?.id,
    provinceId: selectedProvince?.id,
  });
  const primaryCTAOverride =
    bikeModel.configSummaryPrimaryCTAOverride &&
    updateLinkSubmit({
      link: bikeModel.configSummaryPrimaryCTAOverride.url,
      productCode: bikeModel?.productCode,
      colourCode: bikeColour?.colourCode,
      configurationID: loadedConfiguration?.id,
      provinceId: selectedProvince?.id,
    });
  const primaryCTAOverrideText =
    bikeModel.configSummaryPrimaryCTAOverride && bikeModel.configSummaryPrimaryCTAOverride.text;

  const secondaryCTAOverride =
    bikeModel.configSummarySecondaryCTAOverride &&
    updateLinkSubmit({
      link: bikeModel.configSummarySecondaryCTAOverride.url,
      productCode: bikeModel?.productCode,
      colourCode: bikeModel?.colourCode,
      configurationID: loadedConfiguration?.id,
      provinceId: selectedProvince?.id,
    });

  const secondaryCTAText = secondaryCTAOverride ? (
    bikeModel.configSummarySecondaryCTAOverride.text
  ) : (
    <Label name="buyNowLabel" />
  );

  const { configSummarySecondaryCTAOverride } = bikeModel;

  let secondaryCTAUrl = '';

  if (configSummarySecondaryCTAOverride) {
    secondaryCTAUrl = configSummarySecondaryCTAOverride.url;
    if (
      provincePricingEnabled &&
      selectedProvince?.id &&
      selectedProvince.id !== '00000000-0000-0000-0000-000000000000'
    ) {
      const separator = secondaryCTAUrl.includes('?') ? '&' : '?';
      if (!secondaryCTAUrl.includes('provinceid') && !secondaryCTAUrl.includes('provinceId')) {
        secondaryCTAUrl += `${separator}provinceid=${selectedProvince.id.replace(/[{}]/g, '')}`;
      }
    }
  } else if (!buyNowUrl?.includes('ModelCode')) {
    secondaryCTAUrl = `${buyNowUrl}?bikeId=${bikeModel?.id}${
      bikeColour?.id && !loadedConfiguration ? `&colourId=${bikeColour?.id}` : ``
    }${
      selectedProvince?.id && selectedProvince.id !== '00000000-0000-0000-0000-000000000000'
        ? `&provinceid=${selectedProvince.id.replace(/[{}]/g, '')}`
        : ''
    }`;
  } else {
    secondaryCTAUrl = buyNowUrl;
  }

  if (loadedConfiguration?.id && !configSummarySecondaryCTAOverride) {
    secondaryCTAUrl += `&configurationId=${loadedConfiguration.id}&savedConfigurationUrl=${btoa(
      `${window.location.origin}/configure/bike/${bikeModel.id}/configure?configurationId=${loadedConfiguration.id}#config`
    )}`;

    if (
      provincePricingEnabled &&
      selectedProvince?.id &&
      selectedProvince.id !== '00000000-0000-0000-0000-000000000000' &&
      !secondaryCTAUrl.includes('provinceid') &&
      !secondaryCTAUrl.includes('provinceId')
    ) {
      secondaryCTAUrl += `&provinceid=${selectedProvince.id.replace(/[{}]/g, '')}`;
    }
  }

  const [pushedExtra, setPushedExtra] = useState();

  useEffect(() => {
    if (!pushedExtra) {
      return;
    }

    pushToDataLayer(pushedExtra.isSelecting ? 'configuratorExtraSelected' : 'configuratorExtraRemoved', {
      configuratorExtraName: pushedExtra?.extra?.title,
      configuratorExtraPrice: pushedExtra?.extra?.price,
    });

    setPushedExtra();
  }, [pushedExtra]);

  const goToPagePrimary = id => {
    dispatch(setSaving(false));
    window.__configuratorRedirectTarget = true;
    const target = bikeModel.configSummaryPrimaryCTAOverride.target || '_self';
    window.open(
      removeDuplicateParams(
        updateLinkSubmit({
          link: primaryCTAOverride,
          productCode: bikeModel?.productCode,
          colourCode: bikeColour?.colourCode,
          configurationID: id || loadedConfiguration?.id,
          configurationUrl: `${window.location.origin}/configure/bike/${bikeModel.id}/configure?configurationId=${loadedConfiguration?.id}#config`,
          provinceId: selectedProvince?.id,
        }),
        ['configurationID', 'configurationUrl', 'savedConfigurationUrl', 'configurationId']
      ),
      target
    );
  };

  const goToPageSecondary = id => {
    dispatch(setSaving(false));
    window.__configuratorRedirectTarget = true;
    const target = bikeModel?.configSummarySecondaryCTAOverride?.target || '_self';
    let redirectUrl = secondaryCTAUrl;
    const configId = id || loadedConfiguration?.id;
    let existingProvinceId = null;
    const urlMatch = redirectUrl.match(/[?&]provinceid=([^&]+)/i);
    if (urlMatch) {
      [, existingProvinceId] = urlMatch;
    }

    if (secondaryCTAUrl?.includes('start-your-order') && configId) {
      const configUrl = btoa(
        `${window.location.origin}/configure/bike/${bikeModel?.id}/configure?configurationId=${configId}#config`
      );
      const separator = redirectUrl.includes('?') ? '&' : '?';
      redirectUrl += `${separator}configurationId=${configId}&savedConfigurationUrl=${configUrl}`;
    }

    const provinceToUse = selectedProvince?.id || existingProvinceId;
    if (provinceToUse && provinceToUse !== '00000000-0000-0000-0000-000000000000') {
      if (!redirectUrl?.includes('provinceId') && !redirectUrl?.includes('provinceid')) {
        const separator = redirectUrl.includes('?') ? '&' : '?';
        redirectUrl += `${separator}provinceid=${provinceToUse.replace(/[{}]/g, '')}`;
      }
    }

    const updatedLink = updateLinkSubmit({
      link:
        redirectUrl?.includes('ColourCode') && !redirectUrl?.includes('configurationID') && configId
          ? (redirectUrl += '&ConfigurationID={configurationID}&ConfigurationUrl={configurationUrl}')
          : redirectUrl,
      productCode: bikeModel?.productCode,
      colourCode: bikeColour?.colourCode,
      configurationID: configId,
      configurationUrl: `${window.location.origin}/configure/bike/${bikeModel.id}/configure?configurationId=${configId}#config`,
      provinceId: selectedProvince?.id,
    });

    const finalUrl = removeDuplicateParams(updatedLink, [
      'configurationID',
      'configurationUrl',
      'savedConfigurationUrl',
      'configurationId',
      'provinceid',
      'provinceId',
    ]);

    window.open(finalUrl, target);
  };

  const checkFindOrSendToDealer = async () => {
    if (getGarageEmailAddress() && primaryCTAOverride) {
      dispatch(setSaving(true));
      saveToGarage(null, undefined, undefined, configurationId => goToPagePrimary(configurationId));

      return;
    }
    if (ctaLinks.dynamicsForm?.url) {
      window.open(ctaLinks.dynamicsForm.url, ctaLinks.dynamicsForm.target);
    } else if (!primaryCTAOverride) {
      launchSendToDealer(goToPagePrimary);
    } else {
      dispatch(
        openModal('saveToGarage', {
          afterSave: id => goToPagePrimary(id),
        })
      );
    }
  };

  const onSecondaryButton = async () => {
    if (getGarageEmailAddress()) {
      dispatch(setSaving(true));

      saveToGarage(null, undefined, undefined, configurationId => goToPageSecondary(configurationId));
    } else {
      dispatch(
        openModal('saveToGarage', {
          afterSave: id => goToPageSecondary(id),
        })
      );
    }
  };

  const handleSelectServicePack = id => {
    const selectedServiceExtra = extras.servicingPacks.find(service => service.id === id);

    if (selectedServiceExtra) {
      const isSelecting = selectedServicePack?.id !== selectedServiceExtra.id;

      dispatch(isSelecting ? addServicePack(selectedServiceExtra) : removeServicePack());

      setPushedExtra({
        isSelecting,
        extra: {
          configuratorExtraName: selectedServiceExtra?.title,
          configuratorExtraPrice: selectedServiceExtra?.price,
        },
      });
    }
  };

  const handleSelectWarrantyPack = id => {
    const selectedWarrantyExtra = extras.warrantyPacks.find(warranty => warranty.id === id);

    if (selectedWarrantyExtra) {
      const isSelecting = selectedWarrantyPack?.id !== selectedWarrantyExtra.id;

      dispatch(isSelecting ? addWarrantyPack(selectedWarrantyExtra) : removeWarrantyPack());

      setPushedExtra({
        isSelecting,
        extra: {
          configuratorExtraName: selectedWarrantyExtra?.title,
          configuratorExtraPrice: selectedWarrantyExtra?.price,
        },
      });
    }
  };

  const isMobile = useMobileDetect();

  const getSendToDealerLabel = () => {
    // if (!getGarageEmailAddress()) {
    //   return 'saveToGarageLabel';
    // }
    if (primaryCTAOverride) {
      return primaryCTAOverrideText;
    }
    if (ctaLinks.dynamicsForm?.url && ctaLinks.dynamicsForm?.text) {
      return ctaLinks.dynamicsForm.text;
    }
    return ctaLinks.findADealer ? 'findADealerLabel' : 'sendToDealerLabel';
  };

  const secondaryButton = useMemo(
    () => (
      <AdditionalCTA
        as="button"
        onClick={e => {
          onSecondaryButton(e, true, false);
          pushToDataLayer('configuratorSumSec');
          pushToDataLayer('configuratorGarageBuyNow');
        }}
      >
        {secondaryCTAText}
      </AdditionalCTA>
    ),
    [secondaryCTAText, onSecondaryButton, pushToDataLayer]
  );

  return (
    <Wrap mobileOnly={mobileOnly} desktopOnly={desktopOnly}>
      {!provincePricingEnabled &&
        isMobile &&
        options
          .filter(option => !option.hidden)
          .map(({ ...option }) => {
            const linkProps = {};

            if (option.link) {
              linkProps.target = '_blank';
              linkProps.href = option.link;
            } else {
              linkProps.as = 'button';
            }

            if (option.onClick) {
              linkProps.onClick = option.onClick;
            }

            if (option.disabled) {
              linkProps.disabled = option.disabled;
            }

            return (
              <Action key={option.id} {...linkProps}>
                <ActionIcon src={option.icon}></ActionIcon>
                <ActionLabelWrap>
                  <Label name={option.label} />
                </ActionLabelWrap>
              </Action>
            );
          })}
      {!provincePricingEnabled && isMobile && ctaLinks.buyNow && !bikeModel.configSummaryHideSecondaryCTA
        ? secondaryButton
        : null}
      <BikeTitle dangerouslySetInnerHTML={{ __html: bikeModel.name }}></BikeTitle>

      {showTabs && (
        <TabsContainer>
          <Tab active={activeTab === 'addons'} onClick={() => setActiveTab('addons')}>
            <Label name="addOnsTabLabel" />
          </Tab>
          <Tab active={activeTab === 'summary'} onClick={() => setActiveTab('summary')}>
            <Label name="summaryTabLabel" />
          </Tab>
        </TabsContainer>
      )}

      <TabContent active={!showTabs || activeTab === 'addons'}>
        {!showTabs && (
          <>
            {provincePricingEnabled && (
              <>
                <TotalTitle>
                  <TotalTitleLeft>
                    <Label name="msrpLabel" />
                    <InfoCircleWrap
                      onClick={() => {
                        dispatch(setSelectedDisclaimer('msrp'));
                        dispatch(openModal('disclaimer'));
                      }}
                    >
                      <InfoCircle />
                    </InfoCircleWrap>
                  </TotalTitleLeft>
                  <RegularValue>{formatPrice(total)}</RegularValue>
                </TotalTitle>
                <Separator />

                {selectedProvince ? (
                  <>
                    <TotalTitle>
                      <TotalTitleLeft>
                        <Label name="yourProvinceLabel" />
                        <InfoCircleWrap
                          onClick={() => {
                            dispatch(setSelectedDisclaimer('yourProvince'));
                            dispatch(openModal('disclaimer'));
                          }}
                        >
                          <InfoCircle />
                        </InfoCircleWrap>
                      </TotalTitleLeft>
                      <SelectedProvinceText onClick={() => setIsProvinceDropdownOpen(!isProvinceDropdownOpen)}>
                        {selectedProvince.name}
                      </SelectedProvinceText>
                    </TotalTitle>
                    {isProvinceDropdownOpen && (
                      <ProvinceSelector
                        selectedProvince={selectedProvince}
                        onProvinceChange={province => {
                          dispatch(setSelectedProvince(province));
                          setIsProvinceDropdownOpen(false);
                        }}
                      />
                    )}
                  </>
                ) : (
                  <ProvinceSelector
                    selectedProvince={selectedProvince}
                    onProvinceChange={province => {
                      dispatch(setSelectedProvince(province));
                    }}
                  />
                )}

                <TotalTitle>
                  <TotalTitleLeft>
                    <Label name="pdiDealerFeesLabel" />
                    <InfoCircleWrap
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        if (dealerFeeObject) {
                          pushToDataLayer('configuratorExtraInfo', {
                            configuratorExtraName: dealerFeeObject.title,
                            configuratorExtraPrice: dealerFeeObject.price,
                          });
                          dispatch(openModal('accessories'));
                          dispatch(
                            previewAccessory({
                              ...dealerFeeObject,
                              images: [{ src: dealerFeeObject.image.srcLarge }],
                              feeDisclaimer: true,
                              provincialFee: provincialFees?.dealerFee,
                              selectedProvince,
                              provincePricingEnabled,
                            })
                          );
                        }
                      }}
                    >
                      <InfoCircle />
                    </InfoCircleWrap>
                  </TotalTitleLeft>
                  {selectedProvince && provincialFees ? (
                    <RegularValue>{formatPrice(provincialFees.dealerFee || 0)}</RegularValue>
                  ) : (
                    <SelectProvinceText>
                      <Label name="provinceDropdownPlaceholder" />
                    </SelectProvinceText>
                  )}
                </TotalTitle>

                <TotalTitle>
                  <TotalTitleLeft>
                    <Label name="freightLabel" />
                    <InfoCircleWrap
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        if (freightFeeObject) {
                          pushToDataLayer('configuratorExtraInfo', {
                            configuratorExtraName: freightFeeObject.title,
                            configuratorExtraPrice: freightFeeObject.price,
                          });
                          dispatch(openModal('accessories'));
                          dispatch(
                            previewAccessory({
                              ...freightFeeObject,
                              images: [{ src: freightFeeObject.image.srcLarge }],
                              feeDisclaimer: true,
                              provincialFee: provincialFees?.freightFee,
                              selectedProvince,
                              provincePricingEnabled,
                            })
                          );
                        }
                      }}
                    >
                      <InfoCircle />
                    </InfoCircleWrap>
                  </TotalTitleLeft>
                  {selectedProvince && provincialFees ? (
                    <RegularValue>{formatPrice(provincialFees.freightFee || 0)}</RegularValue>
                  ) : (
                    <SelectProvinceText>
                      <Label name="provinceDropdownPlaceholder" />
                    </SelectProvinceText>
                  )}
                </TotalTitle>
                <Separator />

                <TotalTitle className="total-selling-price">
                  <TotalTitleLeft>
                    <Label name="totalSellingPriceLabel" />
                    <InfoCircleWrap
                      onClick={() => {
                        dispatch(setSelectedDisclaimer('price'));
                        dispatch(openModal('disclaimer'));
                      }}
                    >
                      <InfoCircle />
                    </InfoCircleWrap>
                  </TotalTitleLeft>
                  {selectedProvince && provincialFees ? (
                    <TotalSellingPriceValue>{formatPrice(totalPriceIncludingFees)}</TotalSellingPriceValue>
                  ) : (
                    <SelectProvinceText>
                      <Label name="provinceDropdownPlaceholder" />
                    </SelectProvinceText>
                  )}
                </TotalTitle>
                <Separator />

                {provincePricingEnabled && !showTabs && bikeModel.offerSummaryContent && (
                  <OfferBanner offerBanner={bikeModel.offerSummaryContent}></OfferBanner>
                )}

                {selectedProvince && (
                  <>
                    <AdditionalCTAWrap>
                      <AdditionalCTA
                        as="button"
                        onClick={() => [pushToDataLayer('configuratorSumPrim'), checkFindOrSendToDealer()]}
                        primary
                      >
                        <Label name={getSendToDealerLabel()} />
                      </AdditionalCTA>
                      {bikeModel.canTestRide && !ctaLinks.buyNow ? (
                        <AdditionalCTA
                          href={updateLinkSubmit({
                            link: testDriverButtonUrl,
                            productCode: bikeModel?.productCode,
                            colourCode: bikeColour?.colourCode,
                            provinceId: selectedProvince?.id,
                          })}
                          onClick={async event => [
                            await fetchGoal(testRideGoalId),
                            interceptLinkClick(event, () => {
                              pushToDataLayer('configuratorTestRide');
                              pushToDataLayer('configuratorSumSec');
                            }),
                          ]}
                          target="_blank"
                        >
                          <Label name="testRideLabel" />
                        </AdditionalCTA>
                      ) : null}
                      {ctaLinks.buyNow && !bikeModel.configSummaryHideSecondaryCTA ? secondaryButton : null}
                    </AdditionalCTAWrap>

                    <Actions>
                      {options
                        .filter(option => !option.hidden)
                        .map(({ ...option }) => {
                          const linkProps = {};

                          if (option.link) {
                            linkProps.target = '_blank';
                            linkProps.href = option.link;
                          } else {
                            linkProps.as = 'button';
                          }

                          if (option.onClick) {
                            linkProps.onClick = option.onClick;
                          }

                          if (option.disabled) {
                            linkProps.disabled = option.disabled;
                          }

                          return (
                            <Action key={option.id} {...linkProps}>
                              <ActionIcon src={option.icon}></ActionIcon>
                              <ActionLabelWrap>
                                <Label name={option.label} />
                              </ActionLabelWrap>
                            </Action>
                          );
                        })}
                      {shareAndPrintOptions
                        .filter(option => !option.hidden)
                        .map(({ ...option }) => {
                          const linkProps = {};
                          const isZhLanguage = getLanguageCode()
                            ?.toLowerCase()
                            ?.startsWith('zh');
                          if (isZhLanguage && option.id === 'print') {
                            return null;
                          }

                          if (option.link) {
                            linkProps.target = '_blank';
                            linkProps.href = option.link;
                          } else {
                            linkProps.as = 'button';
                          }

                          if (option.onClick) {
                            linkProps.onClick = option.onClick;
                          }

                          if (option.disabled) {
                            linkProps.disabled = option.disabled;
                          }

                          return (
                            <Action key={option.id} {...linkProps}>
                              <ActionIcon src={option.icon}></ActionIcon>
                              <ActionLabelWrap>
                                <Label name={option.label} />
                              </ActionLabelWrap>
                            </Action>
                          );
                        })}
                      {printActions && isIOS() ? (
                        <PopupLabel>
                          <Label name="printGuidanceLabel" />{' '}
                          <a href="App-prefs://prefs:root=SAFARI">
                            <Label name="printGuidanceCta" />
                          </a>
                        </PopupLabel>
                      ) : null}
                    </Actions>
                  </>
                )}
              </>
            )}

            {!provincePricingEnabled && (
              <>
                <TotalTitle>
                  <Label name="otrPriceLabel" />
                  <InfoCircleWrap
                    onClick={() => {
                      dispatch(setSelectedDisclaimer('price'));
                      dispatch(openModal('disclaimer'));
                    }}
                  >
                    <InfoCircle />
                  </InfoCircleWrap>
                </TotalTitle>
                <TotalWrap>
                  <Total>{formatPrice(totalPriceIncludingFees)}</Total>
                  {ctaLinks.finance && bikeModel.canFinance && isSimulationChecksumExists ? (
                    <CalculateFinanceButton onClick={() => [pushToDataLayer('configuratorSumFinance'), openFinance()]}>
                      <CalculatorIcon></CalculatorIcon>
                      <Label name="calculateFinanceLabel"></Label>
                      <i aria-hidden="true" className="icon icon--arrow-right"></i>
                    </CalculateFinanceButton>
                  ) : null}
                </TotalWrap>
              </>
            )}
          </>
        )}
        {extras ? (
          <Extras>
            {extras.servicingPacks.length || extras.warrantyPacks.length ? (
              <ExtrasTitle>
                <Label name="extrasLabel"></Label>
              </ExtrasTitle>
            ) : null}
            {extras.servicingPacks
              ? extras.servicingPacks.map(servicePack => {
                  const selected = selectedServicePack?.id === servicePack.id;

                  return (
                    <Extra key={servicePack.id}>
                      <ExtraIconWrap>
                        <ExtraIcon
                          src={getImageBaseUrl() + servicePack.image.srcSmall}
                          alt={servicePack.image.alt}
                        ></ExtraIcon>
                      </ExtraIconWrap>
                      <ExtraDetails>
                        <ExtraName>
                          {servicePack.title}{' '}
                          <InfoCircleWrap
                            data-testid="infoCircle"
                            onClick={event => [
                              event.stopPropagation(),
                              event.preventDefault(),
                              pushToDataLayer('configuratorExtraInfo', {
                                configuratorExtraName: servicePack.title,
                                configuratorExtraPrice: servicePack.price,
                              }),
                              dispatch(openModal('accessories')),
                              dispatch(
                                previewAccessory({
                                  ...servicePack,
                                  images: [{ src: servicePack.image.srcLarge }],
                                })
                              ),
                            ]}
                          >
                            <InfoCircle />
                          </InfoCircleWrap>
                        </ExtraName>
                        <ExtraPrice>
                          {servicePack.price ? formatPrice(Number(servicePack.price)) : servicePack.priceText}
                        </ExtraPrice>
                      </ExtraDetails>
                      <ExtraCTA selected={selected} onClick={() => handleSelectServicePack(servicePack.id)}>
                        <Label name={selected ? 'selectedLabel' : 'selectLabel'}></Label>
                      </ExtraCTA>
                    </Extra>
                  );
                })
              : null}
            {extras.warrantyPacks
              ? extras.warrantyPacks.map(warrantyPack => {
                  const selected = selectedWarrantyPack?.id === warrantyPack.id;

                  return (
                    <Extra key={warrantyPack.id}>
                      <ExtraIconWrap>
                        <ExtraIcon
                          src={getImageBaseUrl() + warrantyPack.image.srcSmall}
                          alt={warrantyPack.image.alt}
                        ></ExtraIcon>
                      </ExtraIconWrap>
                      <ExtraDetails>
                        <ExtraName>
                          {warrantyPack.title}{' '}
                          <InfoCircleWrap
                            data-testid="infoCircle"
                            onClick={event => [
                              event.stopPropagation(),
                              event.preventDefault(),
                              pushToDataLayer('configuratorExtraInfo', {
                                configuratorExtraName: warrantyPack.title,
                                configuratorExtraPrice: warrantyPack.price,
                              }),
                              dispatch(openModal('accessories')),
                              dispatch(
                                previewAccessory({
                                  ...warrantyPack,
                                  images: [{ src: warrantyPack.image.srcLarge }],
                                })
                              ),
                            ]}
                          >
                            <InfoCircle />
                          </InfoCircleWrap>
                        </ExtraName>
                        <ExtraPrice>
                          {warrantyPack.price ? formatPrice(Number(warrantyPack.price)) : warrantyPack.priceText}
                        </ExtraPrice>
                      </ExtraDetails>
                      <ExtraCTA selected={selected} onClick={() => handleSelectWarrantyPack(warrantyPack.id)}>
                        <Label name={selected ? 'selectedLabel' : 'selectLabel'}></Label>
                      </ExtraCTA>
                    </Extra>
                  );
                })
              : null}
            {!showTabs && !provincePricingEnabled && extras.fees.length ? (
              <>
                <ExtrasTitle>
                  <Label name="feesLabel"></Label>
                </ExtrasTitle>
                {extras.fees.map(fee => {
                  const selected = true;

                  return (
                    <Extra key={fee.id}>
                      <ExtraIconWrap>
                        <ExtraIcon src={getImageBaseUrl() + fee.image.srcSmall} alt={fee.image.alt}></ExtraIcon>
                      </ExtraIconWrap>
                      <ExtraDetails>
                        <ExtraName>
                          {fee.title}{' '}
                          <InfoCircleWrap
                            data-testid="infoCircle"
                            onClick={event => {
                              event.stopPropagation();
                              event.preventDefault();
                              pushToDataLayer('configuratorExtraInfo', {
                                configuratorExtraName: fee.title,
                                configuratorExtraPrice: fee.price,
                              });
                              dispatch(openModal('accessories'));
                              dispatch(
                                previewAccessory({
                                  ...fee,
                                  images: [{ src: fee.image.srcLarge }],
                                  feeDisclaimer: true,
                                })
                              );
                            }}
                          >
                            <InfoCircle />
                          </InfoCircleWrap>
                        </ExtraName>
                        <ExtraPrice>{fee.price ? formatPrice(Number(fee.price)) : fee.priceText}</ExtraPrice>
                      </ExtraDetails>
                      <ExtraCTA selected={selected} disabled>
                        <Label name="selectedLabel"></Label>
                      </ExtraCTA>
                    </Extra>
                  );
                })}
              </>
            ) : null}
          </Extras>
        ) : null}
        {bikeModel.offerSummaryContent ? <OfferBanner offerBanner={bikeModel.offerSummaryContent}></OfferBanner> : null}
        {!showTabs && !provincePricingEnabled && (
          <>
            <AdditionalCTAWrap>
              <AdditionalCTA
                as="button"
                onClick={() => [pushToDataLayer('configuratorSumPrim'), checkFindOrSendToDealer()]}
                primary
              >
                <Label name={getSendToDealerLabel()} />
              </AdditionalCTA>
              {bikeModel.canTestRide && !ctaLinks.buyNow ? (
                <AdditionalCTA
                  href={updateLinkSubmit({
                    link: testDriverButtonUrl,
                    productCode: bikeModel?.productCode,
                    colourCode: bikeColour?.colourCode,
                    provinceId: selectedProvince?.id,
                  })}
                  onClick={async event => [
                    await fetchGoal(testRideGoalId),
                    interceptLinkClick(event, () => {
                      pushToDataLayer('configuratorTestRide');
                      pushToDataLayer('configuratorSumSec');
                    }),
                  ]}
                  target="_blank"
                >
                  <Label name="testRideLabel" />
                </AdditionalCTA>
              ) : null}
              {!isMobile && ctaLinks.buyNow && !bikeModel.configSummaryHideSecondaryCTA ? secondaryButton : null}
            </AdditionalCTAWrap>
          </>
        )}
      </TabContent>

      {showTabs && (
        <TabContent active={activeTab === 'summary'}>
          <TotalTitle>
            <TotalTitleLeft>
              <Label name="msrpLabel" />
              <InfoCircleWrap
                onClick={() => {
                  dispatch(setSelectedDisclaimer('msrp'));
                  dispatch(openModal('disclaimer'));
                }}
              >
                <InfoCircle />
              </InfoCircleWrap>
            </TotalTitleLeft>
            <RegularValue>{formatPrice(total)}</RegularValue>
          </TotalTitle>
          <Separator />

          {selectedProvince ? (
            <>
              <TotalTitle>
                <TotalTitleLeft>
                  <Label name="yourProvinceLabel" />
                  <InfoCircleWrap
                    onClick={() => {
                      dispatch(setSelectedDisclaimer('yourProvince'));
                      dispatch(openModal('disclaimer'));
                    }}
                  >
                    <InfoCircle />
                  </InfoCircleWrap>
                </TotalTitleLeft>
                <SelectedProvinceText onClick={() => setIsProvinceDropdownOpen(!isProvinceDropdownOpen)}>
                  {selectedProvince.name}
                </SelectedProvinceText>
              </TotalTitle>
              {isProvinceDropdownOpen && (
                <ProvinceSelector
                  selectedProvince={selectedProvince}
                  onProvinceChange={province => {
                    dispatch(setSelectedProvince(province));
                    setIsProvinceDropdownOpen(false);
                  }}
                />
              )}
            </>
          ) : (
            <ProvinceSelector
              selectedProvince={selectedProvince}
              onProvinceChange={province => {
                dispatch(setSelectedProvince(province));
              }}
            />
          )}

          <TotalTitle>
            <TotalTitleLeft>
              <Label name="dealerFeesLabel" />
              <InfoCircleWrap
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (dealerFeeObject) {
                    pushToDataLayer('configuratorExtraInfo', {
                      configuratorExtraName: dealerFeeObject.title,
                      configuratorExtraPrice: dealerFeeObject.price,
                    });
                    dispatch(openModal('accessories'));
                    dispatch(
                      previewAccessory({
                        ...dealerFeeObject,
                        images: [{ src: dealerFeeObject.image.srcLarge }],
                        feeDisclaimer: true,
                        provincialFee: provincialFees?.dealerFee,
                        selectedProvince,
                        provincePricingEnabled,
                      })
                    );
                  }
                }}
              >
                <InfoCircle />
              </InfoCircleWrap>
            </TotalTitleLeft>
            {selectedProvince && provincialFees ? (
              <RegularValue>{formatPrice(provincialFees.dealerFee || 0)}</RegularValue>
            ) : (
              <SelectProvinceText>
                <Label name="provinceDropdownPlaceholder" />
              </SelectProvinceText>
            )}
          </TotalTitle>

          <TotalTitle>
            <TotalTitleLeft>
              <Label name="freightFeesLabel" />
              <InfoCircleWrap
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (freightFeeObject) {
                    pushToDataLayer('configuratorExtraInfo', {
                      configuratorExtraName: freightFeeObject.title,
                      configuratorExtraPrice: freightFeeObject.price,
                    });
                    dispatch(openModal('accessories'));
                    dispatch(
                      previewAccessory({
                        ...freightFeeObject,
                        images: [{ src: freightFeeObject.image.srcLarge }],
                        feeDisclaimer: true,
                        provincialFee: provincialFees?.freightFee,
                        selectedProvince,
                        provincePricingEnabled,
                      })
                    );
                  }
                }}
              >
                <InfoCircle />
              </InfoCircleWrap>
            </TotalTitleLeft>
            {selectedProvince && provincialFees ? (
              <RegularValue>{formatPrice(provincialFees.freightFee || 0)}</RegularValue>
            ) : (
              <SelectProvinceText>
                <Label name="provinceDropdownPlaceholder" />
              </SelectProvinceText>
            )}
          </TotalTitle>
          <Separator />

          <TotalTitle className="total-selling-price">
            <TotalTitleLeft>
              <Label name="totalSellingPriceLabel" />
              <InfoCircleWrap
                onClick={() => {
                  dispatch(setSelectedDisclaimer('price'));
                  dispatch(openModal('disclaimer'));
                }}
              >
                <InfoCircle />
              </InfoCircleWrap>
            </TotalTitleLeft>
            {selectedProvince && provincialFees ? (
              <TotalSellingPriceValue>{formatPrice(totalPriceIncludingFees)}</TotalSellingPriceValue>
            ) : (
              <SelectProvinceText>
                <Label name="provinceDropdownPlaceholder" />
              </SelectProvinceText>
            )}
          </TotalTitle>
          <Separator />

          {selectedProvince && (
            <>
              <AdditionalCTAWrap>
                <AdditionalCTA
                  as="button"
                  onClick={() => [pushToDataLayer('configuratorSumPrim'), checkFindOrSendToDealer()]}
                  primary
                >
                  <Label name={getSendToDealerLabel()} />
                </AdditionalCTA>
                {bikeModel.canTestRide && !ctaLinks.buyNow ? (
                  <AdditionalCTA
                    href={updateLinkSubmit({
                      link: testDriverButtonUrl,
                      productCode: bikeModel?.productCode,
                      colourCode: bikeColour?.colourCode,
                      provinceId: selectedProvince?.id,
                    })}
                    onClick={async event => [
                      await fetchGoal(testRideGoalId),
                      interceptLinkClick(event, () => {
                        pushToDataLayer('configuratorTestRide');
                        pushToDataLayer('configuratorSumSec');
                      }),
                    ]}
                    target="_blank"
                  >
                    <Label name="testRideLabel" />
                  </AdditionalCTA>
                ) : null}
                {ctaLinks.buyNow && !bikeModel.configSummaryHideSecondaryCTA ? secondaryButton : null}
              </AdditionalCTAWrap>

              <Actions>
                {options
                  .filter(option => !option.hidden)
                  .map(({ ...option }) => {
                    const linkProps = {};

                    if (option.link) {
                      linkProps.target = '_blank';
                      linkProps.href = option.link;
                    } else {
                      linkProps.as = 'button';
                    }

                    if (option.onClick) {
                      linkProps.onClick = option.onClick;
                    }

                    if (option.disabled) {
                      linkProps.disabled = option.disabled;
                    }

                    return (
                      <Action key={option.id} {...linkProps}>
                        <ActionIcon src={option.icon}></ActionIcon>
                        <ActionLabelWrap>
                          <Label name={option.label} />
                        </ActionLabelWrap>
                      </Action>
                    );
                  })}
                {shareAndPrintOptions
                  .filter(option => !option.hidden)
                  .map(({ ...option }) => {
                    const linkProps = {};
                    const isZhLanguage = getLanguageCode()
                      ?.toLowerCase()
                      ?.startsWith('zh');
                    if (isZhLanguage && option.id === 'print') {
                      return null;
                    }

                    if (option.link) {
                      linkProps.target = '_blank';
                      linkProps.href = option.link;
                    } else {
                      linkProps.as = 'button';
                    }

                    if (option.onClick) {
                      linkProps.onClick = option.onClick;
                    }

                    if (option.disabled) {
                      linkProps.disabled = option.disabled;
                    }

                    return (
                      <Action key={option.id} {...linkProps}>
                        <ActionIcon src={option.icon}></ActionIcon>
                        <ActionLabelWrap>
                          <Label name={option.label} />
                        </ActionLabelWrap>
                      </Action>
                    );
                  })}
                {printActions && isIOS() ? (
                  <PopupLabel>
                    <Label name="printGuidanceLabel" />{' '}
                    <a href="App-prefs://prefs:root=SAFARI">
                      <Label name="printGuidanceCta" />
                    </a>
                  </PopupLabel>
                ) : null}
              </Actions>
            </>
          )}
        </TabContent>
      )}

      {!showTabs && !provincePricingEnabled && (
        <Actions>
          {options
            .filter(option => !option.hidden)
            .map(({ ...option }) => {
              const linkProps = {};

              if (option.link) {
                linkProps.target = '_blank';
                linkProps.href = option.link;
              } else {
                linkProps.as = 'button';
              }

              if (option.onClick) {
                linkProps.onClick = option.onClick;
              }

              if (option.disabled) {
                linkProps.disabled = option.disabled;
              }

              return (
                <Action key={option.id} {...linkProps}>
                  <ActionIcon src={option.icon}></ActionIcon>
                  <ActionLabelWrap>
                    <Label name={option.label} />
                  </ActionLabelWrap>
                </Action>
              );
            })}
          {shareAndPrintOptions
            .filter(option => !option.hidden)
            .map(({ ...option }) => {
              const linkProps = {};
              const isZhLanguage = getLanguageCode()
                ?.toLowerCase()
                ?.startsWith('zh');
              if (isZhLanguage && option.id === 'print') {
                return null;
              }

              if (option.link) {
                linkProps.target = '_blank';
                linkProps.href = option.link;
              } else {
                linkProps.as = 'button';
              }

              if (option.onClick) {
                linkProps.onClick = option.onClick;
              }

              if (option.disabled) {
                linkProps.disabled = option.disabled;
              }

              return (
                <Action key={option.id} {...linkProps}>
                  <ActionIcon src={option.icon}></ActionIcon>
                  <ActionLabelWrap>
                    <Label name={option.label} />
                  </ActionLabelWrap>
                </Action>
              );
            })}
          {printActions && isIOS() ? (
            <PopupLabel>
              <Label name="printGuidanceLabel" />{' '}
              <a href="App-prefs://prefs:root=SAFARI">
                <Label name="printGuidanceCta" />
              </a>
            </PopupLabel>
          ) : null}
        </Actions>
      )}
    </Wrap>
  );
};

export default SummaryActions;
